import React from 'react';
import Icon from '../../../components/AppIcon';

const typeBadge = (type) => {
  if (type === 'bug') return 'bg-red-100 text-red-700 border border-red-200';
  return 'bg-blue-100 text-blue-700 border border-blue-200';
};

const statusBadge = (status) => {
  const map = {
    open: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
    in_progress: 'bg-blue-100 text-blue-700 border border-blue-200',
    resolved: 'bg-green-100 text-green-700 border border-green-200',
    closed: 'bg-gray-100 text-gray-600 border border-gray-200',
  };
  return map?.[status] || map?.open;
};

const priorityBadge = (priority) => {
  const map = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-white',
    low: 'bg-green-500 text-white',
  };
  return map?.[priority] || map?.medium;
};

const statusLabel = (status) => {
  const map = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
  return map?.[status] || status;
};

const BugIssueRow = ({ issue, onEdit, onDelete, onToggleComplete, onStatusChange }) => {
  const isComplete = issue?.status === 'resolved' || issue?.status === 'closed';

  return (
    <tr className={`border-b border-border hover:bg-muted/30 transition-colors ${isComplete ? 'opacity-60' : ''}`}>
      {/* Completion Checkbox */}
      <td className="px-4 py-3 w-10">
        <button
          onClick={() => onToggleComplete(issue?.id)}
          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
            isComplete ? 'bg-green-500 border-green-500' : 'border-border hover:border-primary'
          }`}
          title={isComplete ? 'Mark as open' : 'Mark as resolved'}
        >
          {isComplete && <Icon name="Check" size={12} color="white" />}
        </button>
      </td>
      {/* Title */}
      <td className="px-4 py-3">
        <span className={`text-sm font-medium text-foreground ${isComplete ? 'line-through' : ''}`}>
          {issue?.title}
        </span>
        {issue?.description && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs">{issue?.description}</p>
        )}
      </td>
      {/* Type */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${typeBadge(issue?.type)}`}>
          <Icon name={issue?.type === 'bug' ? 'Bug' : 'Sparkles'} size={11} />
          {issue?.type === 'bug' ? 'Bug' : 'Enhancement'}
        </span>
      </td>
      {/* Status */}
      <td className="px-4 py-3">
        <select
          value={issue?.status}
          onChange={(e) => onStatusChange(issue?.id, e?.target?.value)}
          className={`px-2 py-0.5 rounded-full text-xs font-medium border focus:outline-none cursor-pointer ${statusBadge(issue?.status)}`}
        >
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </td>
      {/* Priority */}
      <td className="px-4 py-3">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${priorityBadge(issue?.priority)}`}>
          {issue?.priority}
        </span>
      </td>
      {/* Due Date */}
      <td className="px-4 py-3">
        <span className="text-sm text-foreground">
          {issue?.dueDate ? new Date(issue.dueDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
        </span>
      </td>
      {/* Requester */}
      <td className="px-4 py-3">
        <span className="text-sm text-foreground">
          {issue?.requester || <span className="text-muted-foreground">—</span>}
        </span>
      </td>
      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(issue)}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Edit"
          >
            <Icon name="Pencil" size={14} />
          </button>
          <button
            onClick={() => onDelete(issue?.id)}
            className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
            title="Delete"
          >
            <Icon name="Trash2" size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default BugIssueRow;
